import React from 'react';

const TermsConditionsEN: React.FC = () => {
  return (
    <div className="prose max-w-none">
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Acceptance of Terms</h2>
        <p className="mb-4">
          By downloading, installing, or using the GatherPlay mobile application and website (the "Service"), you agree 
          to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, do not use the Service.
        </p>
        <div className="bg-gray-100 p-4 rounded-lg mb-4">
          <p><strong>Service Provider:</strong></p>
          <ul className="list-none mt-2 space-y-1">
            <li>Company: BIXMO SRL</li>
            <li>Address: Chisinau, Moldova</li>
            <li>Email: gatherplay.application@gmail.com</li>
          </ul>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Description of Service</h2>
        <p className="mb-4">
          GatherPlay is a multiplayer party game where players act out scenarios based on numbered intensity levels 
          while one player tries to guess who has which number. The Service includes:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Mobile application for iOS and Android devices</li>
          <li>Web application accessible through browsers</li>
          <li>Multiplayer gaming functionality</li>
          <li>Scenario card content</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Age Requirements</h2>
        <p>
          GatherPlay is intended for users aged 6 and above. Users under 13 should use the Service with parental supervision. 
          By using the Service, you represent that you meet the age requirements.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. User Conduct</h2>
        <p className="mb-4">You agree to use the Service responsibly and agree NOT to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Use the Service for any unlawful purpose or in violation of any local, state, national, or international law</li>
          <li>Harass, abuse, or harm other players</li>
          <li>Attempt to reverse engineer, modify, or hack the application</li>
          <li>Use automated scripts or bots to interact with the Service</li>
          <li>Share inappropriate content during gameplay</li>
          <li>Interfere with or disrupt the Service or servers</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Intellectual Property Rights</h2>
        
        <h3 className="text-xl font-semibold text-gray-700 mb-3">5.1 Our Content</h3>
        <p className="mb-4">All content in GatherPlay, including but not limited to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Game mechanics and rules</li>
          <li>Scenario cards and game content</li>
          <li>Software code and algorithms</li>
          <li>Graphics, logos, and design elements</li>
          <li>Text and written materials</li>
        </ul>
        <p className="mb-4">is owned by BIXMO SRL and protected by copyright, trademark, and other intellectual property laws.</p>

        <h3 className="text-xl font-semibold text-gray-700 mb-3">5.2 Your License</h3>
        <p>
          We grant you a limited, non-exclusive, non-transferable license to use the Service for personal, 
          non-commercial purposes in accordance with these Terms.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Premium Features and Payments</h2>
        
        <h3 className="text-xl font-semibold text-gray-700 mb-3">6.1 In-App Purchase</h3>
        <p className="mb-4">
          GatherPlay offers a premium upgrade available through in-app purchase that removes advertisements. 
          This is a one-time purchase tied to your app store account.
        </p>

        <h3 className="text-xl font-semibold text-gray-700 mb-3">6.2 Refunds</h3>
        <p className="mb-4">
          Refund policies are governed by the respective app store (Apple App Store or Google Play Store) policies. 
          We do not process refunds directly.
        </p>

        <h3 className="text-xl font-semibold text-gray-700 mb-3">6.3 Price Changes</h3>
        <p>
          We reserve the right to change the price of premium features at any time, though changes will not affect existing purchases.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Advertising</h2>
        <p>
          The free version of GatherPlay displays advertisements. By using the free version, you consent to viewing these 
          advertisements. Advertisements are provided by third-party networks and we are not responsible for their content.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Data and Privacy</h2>
        <p>
          Your privacy is important to us. Please review our Privacy Policy, which explains how we collect, use, and protect 
          your information. The Privacy Policy is incorporated into these Terms by reference.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Service Availability</h2>
        
        <h3 className="text-xl font-semibold text-gray-700 mb-3">9.1 Availability</h3>
        <p className="mb-4">
          We strive to keep the Service available 24/7, but we do not guarantee uninterrupted access. The Service may be 
          temporarily unavailable due to:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Scheduled maintenance</li>
          <li>Technical difficulties</li>
          <li>Factors beyond our control</li>
        </ul>

        <h3 className="text-xl font-semibold text-gray-700 mb-3">9.2 Updates</h3>
        <p>
          We may update the Service from time to time to add features, fix bugs, or improve performance. Some updates 
          may be required for continued use of the Service.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Limitation of Liability</h2>
        <p className="mb-4">To the maximum extent permitted by applicable law:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>The Service is provided "as is" without warranties of any kind</li>
          <li>We are not liable for any indirect, incidental, special, or consequential damages</li>
          <li>Our total liability shall not exceed the amount you paid for premium features (if any)</li>
          <li>We are not responsible for any loss of data or interruption of service</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless BIXMO SRL from any claims, damages, or expenses arising from your 
          use of the Service or violation of these Terms.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Termination</h2>
        
        <h3 className="text-xl font-semibold text-gray-700 mb-3">12.1 Termination by You</h3>
        <p className="mb-4">You may stop using the Service at any time by uninstalling the application.</p>

        <h3 className="text-xl font-semibold text-gray-700 mb-3">12.2 Termination by Us</h3>
        <p className="mb-4">
          We may terminate or suspend your access to the Service at any time, with or without cause, with or without notice.
        </p>

        <h3 className="text-xl font-semibold text-gray-700 mb-3">12.3 Effect of Termination</h3>
        <p>
          Upon termination, your right to use the Service ceases immediately. Provisions that should survive termination 
          (including intellectual property rights, limitation of liability, and indemnification) will continue to apply.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">13. Governing Law</h2>
        <p>
          These Terms are governed by and construed in accordance with the laws of the Republic of Moldova, without 
          regard to its conflict of law provisions.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">14. Dispute Resolution</h2>
        <p>
          Any disputes arising from these Terms or use of the Service will be resolved through binding arbitration in 
          accordance with the laws of the Republic of Moldova, or in the competent courts of Chisinau, Moldova.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">15. Changes to Terms</h2>
        <p className="mb-4">We reserve the right to modify these Terms at any time. We will notify users of material changes by:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Posting updated Terms within the application</li>
          <li>Updating the "Last Updated" date</li>
          <li>Providing in-app notification for significant changes</li>
        </ul>
        <p>Continued use of the Service after changes constitutes acceptance of the updated Terms.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">16. Severability</h2>
        <p>
          If any provision of these Terms is found to be unenforceable, the remainder of the Terms will remain in full 
          force and effect.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">17. Contact Information</h2>
        <p className="mb-4">For questions about these Terms and Conditions, please contact us:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Email: gatherplay.application@gmail.com</li>
          <li>Company: BIXMO SRL, Chisinau, Moldova</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">18. Entire Agreement</h2>
        <p>
          These Terms, together with our Privacy Policy, constitute the entire agreement between you and BIXMO SRL 
          regarding the use of the Service.
        </p>
      </section>

      <div className="bg-blue-50 p-4 rounded-lg mt-8">
        <p className="text-sm text-blue-800">
          By using GatherPlay, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
        </p>
      </div>
    </div>
  );
};

export default TermsConditionsEN;